/**
 * Permission codes that exist as rows in `admin.permissions` but gate nothing.
 *
 * A tickable box is a promise that ticking it changes what the role can do.
 * These five make no such change: no route, no guard and no component reads
 * them, so a platform manager who ticks one has been told a lie by the screen
 * and will only find out when the staff member cannot do the thing. The role
 * editor therefore does not offer them.
 *
 * The rows stay in the database and inside `PRESET_ROLES`: dropping a code from
 * a seeded role's preset would make the seeder narrow every live role that
 * holds it, and the `role:update` bound in the editor is computed from the same
 * preset — so removing them is a migration and a behaviour change, not a copy
 * fix. Hiding is the whole of the decision.
 *
 * `inert-permissions.test.ts` fails if any of these turns up in the gateway's
 * policy table or its permission-stamp list, which is where a code starts
 * meaning something. When that happens the entry leaves this list rather than
 * gaining an excuse.
 */
export const INERT_PERMISSION_CODES: readonly string[] = [
  // A support ticket is never deleted; it is closed. Nothing deletes one.
  'ticket:delete',
  // Refusing a ticket is `ticket:approve` — the same Finance decision endpoint
  // accepts, completes and rejects. The most misleading of the five: a manager
  // ticks it believing they just handed someone the refuse button.
  'ticket:reject',
  // The ticket queue has no export; nothing downloads it.
  'ticket:export',
  // Filing a ceiling is `approval_limit:update` — one write policy covers POST
  // and PUT alike, so there is no separate create act left to grant.
  'approval_limit:create',
  // The presence heartbeat is deliberately the one back-office write that asks
  // for no permission: a staff member reports their own shift.
  'staff_presence:update',
];

const INERT = new Set(INERT_PERMISSION_CODES);

/** `true` when `code` is a permission the platform never reads. */
export function isInertPermission(code: string): boolean {
  return INERT.has(code);
}
